import { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { getPhToday, getPhNow, formatInPhTime } from './utils/phTime';
import './css/global.css';
import { db, firebaseError } from './firebase';
import TimeSlotSelection from './components/TimeSlotSelection';
// Config -> Theme -> UI Design: Version 2 of the guest booking screen.
import TimeSlotSelectionV2 from './components/TimeSlotSelectionV2';
import BookingIntroV2 from './components/BookingIntroV2';
import BookingInfoBar from './components/BookingInfoBar';
import UserInfoForm from './components/UserInfoForm';
// Checkout (payment) now opens in its own tab — CheckoutPage is that tab,
// CheckoutTabPending is what this tab shows meanwhile.
import CheckoutPage from './components/CheckoutPage';
import CheckoutTabPending from './components/CheckoutTabPending';
// The Open Play join flow's checkout tab (see OpenPlayModal).
import OpenPlayCheckoutPage from './components/OpenPlayCheckoutPage';
import ConfirmationScreen from './components/ConfirmationScreen';
import BookingStatusScreen from './components/BookingStatusScreen';
import TrackBookingModal from './components/TrackBookingModal';
import SportPickerModal from './components/SportPickerModal';
// App.jsx's guest header reuses LandingPage's lp-header/lp-brand/lp-nav/
// lp-book-btn design (see the header markup below), so it needs
// LandingPage's stylesheet too, not just its own.
import './css/LandingPage.css';
import { ref, push, update, orderByChild, equalTo, onValue, query, get } from "firebase/database";
import Modal from './components/Modal';
import LandingPage from './components/LandingPage';
import { formatMoney } from './utils/currencyUtil';
import { useConfig, ConfigProvider, ConfigOverride } from './context/ConfigContext';
import { COURT_NAME, NAV_LINKS, CHECKOUT_ROUTE_PATH, OPEN_PLAY_CHECKOUT_ROUTE_PATH, ADMIN_ROUTE_PATH, POS_ROUTE_PATH, UI_PREVIEW_ROUTE_PATH, UI_DESIGN_CONFIG_KEY, UI_DESIGN_VERSIONS, SHOW_COURT_RULES_CONFIG_KEY, COURT_REMINDERS_TEXT_CONFIG_KEY } from './constants/bookingConst';
// Guest-facing "Join this session" flow for an Open Play cell/card — session
// details, the list of players coming, checkout and confirmation.
import OpenPlayModal from './components/OpenPlayModal';
// Shown in place of any loading spinner that runs past the configured
// timeout (REACT_APP_LOADING_TIMEOUT_SECONDS) — see hooks/useLoadingTimeout.js.
import ConnectionErrorScreen from './components/ConnectionErrorScreen';
import { useLoadingTimeout } from './hooks/useLoadingTimeout';
import { useCheckoutTab } from './hooks/useCheckoutTab';
import { releasePendingBooking, isBookingSubmitted } from './utils/checkoutFlowUtil';
import { msUntil } from './utils/countdownUtil';

import { getCourtRate, loadingSpinner, spinnerRing, findConflictsAgainstBookedSlots, parseTimeSlotStartHour, formatTimeSlotRange, isCourtAvailable, getCourtLabel, getMapLinkUrl, generateConfirmationId, getOwnershipSelectionBlock, getAddOnSelectionBlock, getOrphanedAddOnCourts, calculateReservationFee, getWeekdayFromDateStr, getTimeSlotsForCourt } from './utils/bookingUtils';
// Court rules the guest accepts between their details and checkout.
import CourtRulesSheet from './components/CourtRulesSheet';
import { getOpenPlaySessionForCell, formatOpenPlaySessionDetails, nextDateStr } from './utils/openPlayUtil';
import { buildGuestPartialPaymentFields } from './utils/partialPaymentUtil';
// Event packages — whole-venue bookings sold as a block of hours. The three
// screens in front of the existing details/checkout/confirmation modals.
import {
  PackageEntryButton, PackagePickerModal, PackageOptionPickerModal, PackageDetailsModal, PackageDateTimeModal,
} from './components/PackageBookingModals';
import {
  getActivePackages,
  getPackagePrice,
  getSelectablePackageDurations,
  getPackageOptionChoices,
  getPackageOptionById,
  packageIsHourlyPriced,
  BOOKING_SOURCE_PACKAGE,
} from './utils/packageUtils';
import { buildPackageTimeSlots } from './utils/packageAvailabilityUtil';
// Config -> Booking Settings: block bookings are selected whole, and the
// minimum / connecting-hours rules are re-checked before checkout.
import { getBlockSlotsForCell, getSelectionRuleIssue } from './utils/bookingRulesUtil';
import { buildBookedSlotsFromSnapshot, claimBookingSlots, releaseBookingSlots, isSlotClosed } from './utils/bookingAvailabilityUtil';
import { getBookingHourRange, claimEquipmentRental, releaseEquipmentRental } from './utils/equipmentAvailabilityUtil';
import AdminPage from './components/AdminPage';
import PosPage from './components/PosPage';
import BrandLogo from './components/BrandLogo';
// "Open in your browser" nudge for guests arriving from social-media links.
import InAppBrowserBanner from './components/InAppBrowserBanner';
// Popup asking guests inside Facebook's in-app browser to switch browsers,
// in the version picked under Config -> Theme -> Open in Browser Popup.
import FacebookBrowserPrompt from './components/FacebookBrowserPrompt';
// Preview of that popup and its manual-steps guide, viewable in any browser.
import BrowserUiPreviewPage from './components/BrowserUiPreviewPage';
import AppFooter from './components/AppFooter';
import { AuthGuard } from './components/AuthGuard';
import { AuthProvider, useAuthStatus } from './context/AuthContext';
import FirstVisitSplash from './components/FirstVisitSplash';
import { ChevronLeft, ChevronRight, X, Menu, LayoutGrid } from 'lucide-react';

/**
 * Title for the "Join Open Play" dialog, with the venue name kept on one
 * line.
 *
 * A multi-word venue ("Park 'n Dink") was breaking across lines mid-name —
 * "…at Park" / "'n Dink" — which reads as two different places. Joining its
 * words with non-breaking spaces makes the name a single unbreakable token,
 * so it moves to the next line whole when it doesn't fit. `overflow-wrap`
 * on .modal-title (Modal.css) is the backstop for a name too long to fit on
 * a line by itself.
 *
 * @param {string} venue
 * @returns {string}
 */
const joinOpenPlayTitle = (venue) =>
  `Join Open Play at ${String(venue || '').replace(/ /g, ' ')}`;

// Shown when the admin hasn't uploaded any court photos yet (Admin > Photos tab).
const DEFAULT_GALLERY_PHOTOS = [
  { id: 'demo-main', url: '/images/gallery/court-main.jpg' },
  { id: 'demo-2', url: '/images/gallery/court-2.jpg' },
  { id: 'demo-3', url: '/images/gallery/court-3.jpg' },
];
const MAX_GALLERY_TILES = 4; // collage shows at most 1 large + 3 stacked photos

// ==================== 1. MAIN APP WORKER ====================
// This sub-component safely sits INSIDE the ConfigProvider, so it can grab the config!
function BookingApp({ standalone }) {
  const { config: liveConfig, loading: configLoading, landingConfig } = useConfig();
  // While the live config is loading (App Check + sign-in + the read), the
  // guest screens — landing page AND booking grid — render from
  // landingConfig: this visitor's cached config, else the build-time
  // snapshot. The guest config is part of the build, so the live read is a
  // background refresh only: nothing on the guest screens waits for it (see
  // `bookingReady`). Once loaded, this is exactly the live config.
  const config = (configLoading && landingConfig) ? landingConfig : liveConfig;
  // For images only: landingConfig points images that are unchanged since the
  // deploy at their static files (public/snapshot/*), so photos do not blink
  // when the live config lands. Never used for anything that is saved or sent.
  const displayConfig = landingConfig || config;
  // Sign-in no longer blocks the whole app (see AuthProvider): the landing
  // page renders from the cache/build snapshot while it runs, and only the
  // database reads below wait for it.
  const { ready: authReady, failed: authFailed } = useAuthStatus();

  // Watchdog over that boot load. Firebase never reports "unreachable" — on a
  // dead or captive-portal connection onValue simply never calls back — so
  // without this the guest sits on "Preparing your experience" forever with
  // nothing to act on. Must stay up here with the other hooks: the early
  // return that consumes it is below, and a hook called after it would change
  // the call order between renders.
  // Timed from sign-in, not from mount: sign-in has its own LOADING_TIMEOUT_MS
  // watchdog in AuthProvider, so each phase keeps the full budget it had when
  // AuthGuard blocked the whole app. Starting this at mount would give App
  // Check + sign-in + the config read one shared budget, and a slow phone
  // that used to get through would now hit the error screen.
  // No restart needed: the boot timeout screen's Refresh hard-reloads the
  // whole page (see ConnectionErrorScreen), which starts every watchdog over.
  // Only blocks the app when there is no cached/build config to render from
  // (see the early return below) — otherwise a stalled background refresh is
  // simply ignored.
  const { timedOut: configTimedOut } = useLoadingTimeout(configLoading && authReady);

  // True once sign-in is in — the database rules gate every read and write on
  // it. Deliberately NOT waiting on the live config: the guest config ships
  // with the build (snapshot), so the grid and its actions run on that while
  // the live copy refreshes in the background. Until then actions that read
  // or write booking data return early.
  const bookingReady = authReady;

  // "Book a Court" on the standalone /bookings screen is a full page
  // navigation (different route, no shared SPA state) to `/?book=1` — this
  // reads that signal on first mount so we land straight on TimeSlotSelection
  // instead of the landing page. See the cleanup effect below.
  const [showLandingPage, setShowLandingPage] = useState(() => {
    if (typeof window !== 'undefined' && /(?:^|[?&])book=1(?:&|$)/.test(window.location.search)) {
      return false;
    }
    // A build/cached config with the landing page switched off opens straight
    // on the grid from the very first render, instead of a spinner frame
    // while the effect further down catches up.
    if (!standalone && landingConfig?.showLandingPage === false) return false;
    return true;
  });
  // Date selection is now combined with the Time Slot step (no separate Date screen).
  const [step, setStep] = useState(2);
  const [selectedDate, setSelectedDate] = useState(() => getPhToday());
  // Accumulates selections across MULTIPLE dates now — { [dateStr]: {
  // [timeSlot]: court[] } } — instead of just one date. `selectedDate`
  // above is only which date's grid is currently being *viewed*; switching
  // it no longer clears anything (see handleDateSelect below).
  const [selectedDates, setSelectedDates] = useState({});
  // Which sport (Config -> Sports & Courts) the guest is currently
  // booking. Defaults to the first configured sport, so a single-sport
  // club behaves exactly as before with no picker ever shown — see
  // hasMultipleSports/handleSportSelect below and LandingPage's sport
  // picker modal.
  const [selectedSportId, setSelectedSportId] = useState(null);
  useEffect(() => {
    if (!selectedSportId && config?.sports?.[0]?.id) {
      setSelectedSportId(config.sports[0].id);
    }
  }, [config?.sports, selectedSportId]);
  const hasMultipleSports = (config?.sports?.length || 0) > 1;
  // Admin can switch the landing page off entirely (Config -> System), in
  // which case there's nowhere for the header brand to navigate to — see
  // the header markup below and handleBackToHome, which deliberately stays
  // on the booking screen in that mode. Only `false` disables it; an older
  // config that predates the field has it undefined and should still get a
  // landing page.
  const landingPageEnabled = config?.showLandingPage !== false;
  const [showSportPicker, setShowSportPicker] = useState(false);
  // Guest tapped an "Open Play (Click to join)" cell on the Time Slot
  // Selection grid (TimeSlotSelection.jsx's onJoinOpenPlay). The automated
  // Open Play booking/join flow isn't live yet, so instead of opening
  // OpenPlayModal this looks up that cell's details (an admin-hosted
  // session's fee/cap/notes if there is one — see AdminHostOpenPlayModal —
  // otherwise the sport's generic openPlayPricePerPlayer/openPlayNote) and
  // surfaces them via the shared Modal (showModal, defined below), whose
  // confirm button opens the real join flow (OpenPlayModal) below.
  const handleJoinOpenPlaySlot = async (dateStr, timeSlot, court) => {
    // Display-only until sign-in is in (bookingReady).
    if (!bookingReady) return;
    let session = null;
    try {
      session = await getOpenPlaySessionForCell(dateStr, timeSlot, court);
    } catch (err) {
      console.error('Error looking up Open Play session:', err);
    }

    let detailLines;
    if (session) {
      // The cell belongs to a real hosted session — show that session's
      // full details (every court/time it covers, not just this one cell),
      // same as clicking its card directly (see handleOpenPlaySessionClick).
      detailLines = formatOpenPlaySessionDetails(session, config);
    } else {
      // A plain per-cell "Open Play" status with no session record behind
      // it (legacy, or set some other way) — fall back to just this cell's
      // own date/time/court plus the sport's generic fee/notes.
      const sportName = activeSport?.name || 'Court';
      const fee = Number(activeSport?.openPlayPricePerPlayer) || 0;
      const notes = activeSport?.openPlayNote;
      const dateLabel = dateStr
        ? new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : '';
      const timeLabel = timeSlot ? formatTimeSlotRange(timeSlot) : '';
      detailLines = [
        `${sportName} Open Play`,
        [dateLabel, timeLabel, court != null ? getCourtLabel(config, court) : ''].filter(Boolean).join(' · '),
        `Fee: ${formatMoney(fee, config)} / player`,
        notes || '',
      ].filter(Boolean).join('\n');
    }

    showModal({
      title: joinOpenPlayTitle(config?.courtName || COURT_NAME),
      message: detailLines,
      type: 'info',
      confirmText: 'Join this session',
      showCancel: true,
      cancelText: 'Not now',
      // Hand off to the real join flow. The peek stays a peek — nothing is
      // written until the guest has named their players and reached the
      // payment step inside OpenPlayModal.
      onConfirm: () => {
        closeModal();
        setOpenPlayJoin({
          session,
          dateStr,
          timeSlot,
          sportId: session?.sportId || activeSport?.id || null,
        });
      },
    });
  };

  // Guest tapped a "Hosted open play sessions" card (see
  // OpenPlaySessionsSection.jsx, rendered below the grid in
  // TimeSlotSelection.jsx) — same details-then-join path as tapping a grid
  // cell above, just keyed by the session directly instead of needing to
  // look one up for a specific cell. No timeSlot: a card covers the whole
  // session, which can span several hours and courts.
  const handleOpenPlaySessionClick = (session) => {
    if (!bookingReady) return;
    showModal({
      title: joinOpenPlayTitle(config?.courtName || COURT_NAME),
      message: formatOpenPlaySessionDetails(session, config),
      type: 'info',
      confirmText: 'Join this session',
      showCancel: true,
      cancelText: 'Not now',
      onConfirm: () => {
        closeModal();
        setOpenPlayJoin({
          session,
          dateStr: session?.date || null,
          timeSlot: null,
          sportId: session?.sportId || activeSport?.id || null,
        });
      },
    });
  };

  // The Open Play join flow currently on screen, or null. Holds the clicked
  // cell/card's context — the hosted session record (null for a plain
  // per-cell "Open Play" status), its date, the specific hour if a grid cell
  // was what got clicked, and which sport's pricing applies.
  const [openPlayJoin, setOpenPlayJoin] = useState(null);
  const [bookedSlots, setBookedSlots] = useState({});
  // { [timeSlot]: { [court]: status } } for the currently-selected date —
  // see the slotOverrides listener below.
  const [slotOverrides, setSlotOverrides] = useState({});
  // The NEXT date's bookings and overrides, for the after-midnight rows the
  // grid shows when the club trades past midnight (Config -> Pricing -> Open
  // Past Midnight Until). Those rows are real next-date cells, so they need
  // that date's data — reading them out of the selected date's maps would
  // show a completely different hour's bookings.
  const [nextDayBookedSlots, setNextDayBookedSlots] = useState({});
  const [nextDaySlotOverrides, setNextDaySlotOverrides] = useState({});
  const [formData, setFormData] = useState({ fullName: '', email: '', phone: '' });
  const [errors, setErrors] = useState({});
  const [bookingData, setBookingData] = useState(null);
  // One Firebase record id per selected date (see createPendingBooking) —
  // index-aligned with bookingData.dates. Was a single `bookingKey` before
  // multi-date support.
  const [bookingKeys, setBookingKeys] = useState(null);
  const [isCreatingBooking, setIsCreatingBooking] = useState(false);
  // True while a step transition (Continue to Details / submit details /
  // Payment Done) is in flight — shows a spinner inside the step modal
  // instead of the outgoing/incoming step's content, so the modal never
  // flashes empty or jumps straight from one card to the next.
  const [stepLoading, setStepLoading] = useState(false);
  // The grid runs on the build config, but the details/checkout steps need
  // the live one (payment settings and other fields the snapshot leaves
  // out). A Continue tapped before the background refresh has landed is
  // parked here by key and replayed once it does — through a ref, so the
  // handler that runs is the one rendered WITH the live config, not the
  // stale closure from the tap. See runWithLiveConfig.
  const [pendingLiveConfigAction, setPendingLiveConfigAction] = useState(null);
  // Set while the court rules sheet is open between the guest details and
  // checkout; holds the rental picked just before it, for "I Understand" to
  // carry on with. Null when the sheet is closed.
  const [pendingCourtRules, setPendingCourtRules] = useState(null);
  const liveConfigActionsRef = useRef({});
  useEffect(() => {
    if (!pendingLiveConfigAction || configLoading) return;
    setPendingLiveConfigAction(null);
    liveConfigActionsRef.current[pendingLiveConfigAction]?.();
  }, [pendingLiveConfigAction, configLoading]);
  // ---- Event package flow ----
  // `stage` is which of the three package screens is open (null = none).
  // Kept in one object so closing the flow is a single setState and can never
  // leave a half-open state behind.
  const [pkgFlow, setPkgFlow] = useState(null);
  // Bookings for the package's chosen date. The main `bookedSlots` listener
  // follows `selectedDate` (the grid the guest is looking at), which is not
  // necessarily the date they picked for their event — so this is read
  // separately rather than hijacking that subscription.
  const [pkgBookedSlots, setPkgBookedSlots] = useState({});
  const [pkgLoadingSlots, setPkgLoadingSlots] = useState(false);
  // Set once the guest confirms date + time; drives pricing and the extra
  // fields written onto the booking record. Null for an ordinary booking, so
  // every code path below stays exactly as it was.
  const [packageBooking, setPackageBooking] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Starts true: the grid's first paint is before sign-in, when the bookings
  // read has not even started, and the cells must not show as free then.
  const [isFetchingSlots, setIsFetchingSlots] = useState(true);
  // Bumped by Refresh on the grid's timeout screen, to tear the date's
  // bookings subscription down and open a fresh one — onValue on an
  // unreachable database never calls back, so re-subscribing is the only
  // real retry.
  const [slotsAttempt, setSlotsAttempt] = useState(0);
  // Same watchdog the boot screen uses, over the per-date grid fetch: a
  // stalled read would otherwise leave the guest on a spinner with a date
  // strip they can still tap, which just stacks more stalled reads.
  // Timed from sign-in, not mount — sign-in has its own watchdog
  // (AuthProvider), so the grid's budget covers the bookings read alone.
  const { timedOut: slotsTimedOut, restart: restartSlotsTimeout } = useLoadingTimeout(isFetchingSlots && authReady);

  /**
   * Refresh on the grid's timeout screen — restarts the countdown and
   * re-subscribes to the selected date's bookings.
   *
   * @returns {void}
   */
  const handleSlotsRetry = () => {
    restartSlotsTimeout();
    setSlotsAttempt((n) => n + 1);
  };
  const [lightboxIndex, setLightboxIndex] = useState(null);
  // Mobile nav-menu toggle for the lp-header (mirrors LandingPage's own).
  const [navOpen, setNavOpen] = useState(false);
  // "Track Booking" now opens a modal instead of navigating to the
  // standalone /bookings page, so a guest mid-booking-flow (or on the
  // landing page) doesn't lose their place — see TrackBookingModal.
  const [showTrackBookingModal, setShowTrackBookingModal] = useState(false);
  // Section id (e.g. 'rates') to scroll to once we're back on LandingPage,
  // set when a guest taps a nav link from a booking screen — see
  // handleBackToHome below and LandingPage's scrollToId prop.
  const [pendingScrollId, setPendingScrollId] = useState(null);

  // The "Book a Court" hero collage (Time Slot Selection screen) shows the
  // active sport's own photos when it has any (Config -> Photos -> Per-Sport
  // Photo Galleries), falling back to the shared club-wide gallery, then the
  // bundled demo photos — same fallback chain as before for a club that
  // hasn't set up per-sport photos (or only has one sport).
  // displayConfig, not config: photos only, so unchanged ones stay on their
  // static snapshot files (no blink when the live config lands).
  //
  // Per-sport photos only count while the club has more than one sport: that
  // is the only time Config -> Photos shows (and lets an admin edit) them. A
  // club that dropped back to one sport keeps the old per-sport list in its
  // config, hidden from the admin, and it used to override the shared gallery
  // the admin can actually see and change.
  const activeSport = (displayConfig?.sports || []).find(s => s.id === selectedSportId);
  const sportGalleryPhotos = (displayConfig?.sports || []).length > 1 ? (activeSport?.galleryPhotos || []) : [];
  const galleryPhotos = sportGalleryPhotos.length > 0
    ? sportGalleryPhotos
    : (displayConfig?.galleryPhotos && displayConfig.galleryPhotos.length > 0)
      ? displayConfig.galleryPhotos
      : DEFAULT_GALLERY_PHOTOS;
  const visibleGalleryPhotos = galleryPhotos.slice(0, MAX_GALLERY_TILES);
  const mainGalleryPhoto = visibleGalleryPhotos[0];
  const sideGalleryPhotos = visibleGalleryPhotos.slice(1);

  // Config -> Theme -> UI Design. Read from displayConfig, like the theme
  // colours, so the design only changes for guests once the build that
  // carries it is deployed — never mid-visit when the live config lands.
  const isUiDesignV2 = displayConfig?.[UI_DESIGN_CONFIG_KEY] === UI_DESIGN_VERSIONS.V2;
  const BookingTimeSlotScreen = isUiDesignV2 ? TimeSlotSelectionV2 : TimeSlotSelection;

  // Checkout runs in its own tab. The handlers it calls are defined further
  // down (after the loading early-returns), so they're reached through a ref
  // that is filled in on each full render.
  const checkoutHandlersRef = useRef({});
  // Set while this tab is releasing a reservation itself, so the deadline,
  // the tab-closed check and the cancel button can't overlap.
  const releasingRef = useRef(false);
  const checkoutTab = useCheckoutTab({
    onReceiptUploaded: (data) => checkoutHandlersRef.current.onReceiptUploaded?.(data),
    onSubmitted: (data) => checkoutHandlersRef.current.onSubmitted?.(data),
    onCancelled: () => checkoutHandlersRef.current.onCancelled?.(),
    onExpired: () => checkoutHandlersRef.current.onExpired?.(),
    onTabClosed: () => checkoutHandlersRef.current.onTabClosed?.(),
  });

  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    confirmText: 'OK',
    onConfirm: null,
    showCancel: false,
    cancelText: 'Cancel',
    hideIcon: false
  });

  // A one-time read of the package date's bookings, so the date/time screen
  // can grey out start times that clash. Deliberately not a live listener:
  // this screen is open for seconds, and the authoritative check is still the
  // slot-lock transaction at booking time (claimBookingSlots).
  useEffect(() => {
    const dateStr = pkgFlow?.dateStr;
    if (!dateStr || !db || !authReady) { setPkgBookedSlots({}); return; }
    let cancelled = false;
    setPkgLoadingSlots(true);
    get(query(ref(db, 'bookings'), orderByChild('date'), equalTo(dateStr)))
      .then((snap) => { if (!cancelled) setPkgBookedSlots(buildBookedSlotsFromSnapshot(snap)); })
      .catch(() => { if (!cancelled) setPkgBookedSlots({}); })
      .finally(() => { if (!cancelled) setPkgLoadingSlots(false); });
    return () => { cancelled = true; };
  }, [pkgFlow?.dateStr, authReady]);

  // Live-updating bookedSlots for the selected date — an onValue listener
  // rather than a one-time fetch, so if another guest (or the admin) books,
  // cancels, or gets approved/declined while this guest is sitting on the
  // Time Slot screen, the grid reflects it immediately instead of only
  // after a manual refresh. isFetchingSlots is only toggled around the
  // *initial* snapshot for a given date, so later live updates don't flash
  // the full-page spinner.
  useEffect(() => {
    // No database at all (Firebase failed to initialise) — nothing will ever
    // arrive, so don't leave the overlay up waiting for it.
    if (!db) {
      setIsFetchingSlots(false);
      return;
    }
    // Not before sign-in: the rules would deny the query, and its error path
    // leaves an empty grid that reads as "everything is free".
    if (!selectedDate || !authReady) return;

    setIsFetchingSlots(true);
    const dateStr = formatInPhTime(selectedDate, 'yyyy-MM-dd');
    const bookingsRef = ref(db, 'bookings');
    const q = query(bookingsRef, orderByChild('date'), equalTo(dateStr));

    let receivedFirstSnapshot = false;
    const unsubscribe = onValue(q, (querySnapshot) => {
      setBookedSlots(buildBookedSlotsFromSnapshot(querySnapshot));
      if (!receivedFirstSnapshot) {
        receivedFirstSnapshot = true;
        setIsFetchingSlots(false);
      }
    }, (error) => {
      console.error('Error listening to bookings:', error);
      setIsFetchingSlots(false);
    });

    return () => unsubscribe();
  }, [selectedDate, slotsAttempt, authReady]);

  // Admin-set per-cell status overrides for the selected date (Admin ->
  // Manage availability grid — see AdminTimeSlotSelection.jsx). Guests react
  // to two values here (both named in bookingConst.js):
  //
  //   'openPlay' — TimeSlotSelection.jsx swaps that cell's normal booking
  //                button for a "Join Open Play" one
  //   'closed'   — that cell is blocked exactly like a switched-off court
  //
  // The other override values (processing/passed/booked-without-a-real-
  // booking) stay admin-side only, same as before this listener existed.
  useEffect(() => {
    if (!selectedDate || !db || !authReady) return;
    const dateStr = formatInPhTime(selectedDate, 'yyyy-MM-dd');
    const overridesRef = ref(db, `slotOverrides/${dateStr}`);
    const unsubscribe = onValue(overridesRef, (snapshot) => {
      setSlotOverrides(snapshot.exists() ? snapshot.val() : {});
    }, (error) => {
      console.error('Error listening to slot overrides:', error);
    });
    return () => unsubscribe();
  }, [selectedDate, authReady]);

  // The same two listeners again for the FOLLOWING date, feeding the grid's
  // after-midnight rows. Subscribed unconditionally rather than only when
  // such rows exist: whether they do depends on config that can change under
  // an open tab, and two listeners on one date each cost far less than a grid
  // showing an hour as free that somebody has already booked.
  useEffect(() => {
    if (!selectedDate || !db || !authReady) return undefined;
    const dateStr = nextDateStr(formatInPhTime(selectedDate, 'yyyy-MM-dd'));
    if (!dateStr) return undefined;

    const unsubscribeBookings = onValue(
      query(ref(db, 'bookings'), orderByChild('date'), equalTo(dateStr)),
      (snap) => setNextDayBookedSlots(buildBookedSlotsFromSnapshot(snap)),
      (error) => { console.error('Error listening to next-day bookings:', error); setNextDayBookedSlots({}); },
    );
    const unsubscribeOverrides = onValue(
      ref(db, `slotOverrides/${dateStr}`),
      (snap) => setNextDaySlotOverrides(snap.exists() ? snap.val() : {}),
      (error) => { console.error('Error listening to next-day slot overrides:', error); setNextDaySlotOverrides({}); },
    );
    return () => { unsubscribeBookings(); unsubscribeOverrides(); };
  }, [selectedDate, slotsAttempt, authReady]);

  // Once the ?book=1 signal above has been consumed, strip it from the URL
  // so it doesn't linger (e.g. get shared/bookmarked) or re-trigger anything.
  useEffect(() => {
    if (/(?:^|[?&])book=1(?:&|$)/.test(window.location.search)) {
      window.history.replaceState(null, '', window.location.pathname + window.location.hash);
    }
  }, []);

  // Admin can disable the landing page entirely (Config -> System ->
  // "Show landing page") so guests land straight on the court/time grid
  // instead. The initial showLandingPage state above can't see this yet —
  // config loads asynchronously — so once it resolves, apply it here, but
  // only the first time (a ref guard, not a state one, so this doesn't
  // fire again and yank the guest back if they've already started
  // navigating by the time config finishes loading).
  //
  // Read from `config`, which during the live load is the cached/snapshot
  // copy — so a grid-first club opens straight on the grid instead of flashing
  // the landing page. A "disabled" value is applied as soon as it is seen; an
  // "enabled" one only counts once the live config confirms it, in case the
  // admin switched it off since the snapshot was taken.
  const appliedLandingPageConfigRef = useRef(false);
  const landingPageSetting = config?.showLandingPage;
  useEffect(() => {
    if (standalone || appliedLandingPageConfigRef.current) return;
    if (landingPageSetting === false) {
      appliedLandingPageConfigRef.current = true;
      setShowLandingPage(false);
      setStep(2);
      return;
    }
    if (!configLoading) appliedLandingPageConfigRef.current = true;
  }, [standalone, configLoading, landingPageSetting]);

  // Close the mobile nav menu on outside click (mirrors LandingPage's own).
  useEffect(() => {
    if (!navOpen) return;
    const handleClickOutside = (event) => {
      if (!event.target.closest('.lp-header')) setNavOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [navOpen]);

  // Hold the loading screen until Firebase has actually returned the club's
  // config. ConfigProvider seeds `config` with DEFAULT_CONFIG so consumers
  // never see undefined, which means rendering before `loading` flips false
  // paints the whole page with placeholder values (generic court name, demo
  // gallery, default sports/rates) for a few frames and then swaps them —
  // the flash this fixes. Must stay below every hook above: an early return
  // placed among them would change the hook call order between renders.
  //
  // The timed-out case is checked FIRST, and deliberately does NOT also test
  // `configLoading`. Once the guest has been told the connection failed, the
  // error modal stays up until they press its button — a config that finally
  // arrives 30 seconds late must not silently dismiss it under someone who is
  // still reading it.
  //
  // The error is a modal over whatever the guest was looking at: the landing
  // page or grid when they can render early (see the main return below), or
  // the "Preparing your experience" spinner when there is nothing to draw yet.
  //
  // EXCEPTION — the landing page and the booking grid. They do not wait for
  // the live config when they can render from this visitor's cached config or
  // the build-time snapshot (landingConfig): the live read is a background
  // refresh, and the grid waits only on sign-in and the selected date's
  // bookings. For them a stalled config read is ignored rather than shown as
  // an error. The standalone /bookings page still waits, as does a first
  // visit with no snapshot. A failed sign-in always lands on the error screen
  // — without it no booking data can be read at all.
  //
  // A landing page the snapshot says is disabled is never drawn early — the
  // effect above is about to switch this guest to the grid.
  const canShowEarly = !standalone
    && !!landingConfig
    && (!showLandingPage || landingConfig.showLandingPage !== false);

  const connectionFailed = authFailed
    || (configTimedOut && (!canShowEarly || pendingLiveConfigAction));

  if (connectionFailed && !canShowEarly) {
    return (
      <>
        {loadingSpinner('Preparing your experience', landingConfig?.logoUrl || config?.logoUrl)}
        <ConnectionErrorScreen />
      </>
    );
  }

  if (configLoading && !canShowEarly) {
    return loadingSpinner('Preparing your experience', landingConfig?.logoUrl || config?.logoUrl);
  }

  // Switching the viewed date no longer resets the guest's picks — they now
  // accumulate across dates (see selectedDates above), so this just changes
  // which date's grid is on screen.
  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  // `dateStr` is whichever date's grid the guest tapped a cell on (passed
  // straight through from TimeSlotSelection's onCourtToggle, which also
  // doubles as "remove" when called from a summary row's x button — see
  // TimeSlotSelection.jsx).
  const handleCourtToggle = (dateStr, timeSlot, courtNumber) => {
    if (!dateStr) return;

    // A block booking (Config -> Booking Settings) is selected and released
    // as a whole: tapping its button, or removing any one of its hours from
    // the summary, moves EVERY hour of the block on that court together.
    // Anything outside a block is a block of one, so the rest of this
    // handler is the same either way.
    const blockInfo = getBlockSlotsForCell(config, dateStr, timeSlot, courtNumber);
    const targetSlots = blockInfo ? blockInfo.slots : [timeSlot];

    // Shared ownership (Config -> System): co-owned clubs pay each court's
    // owner into their own account, so one booking — which is one payment —
    // can only ever contain one owner's courts. Courts with no owner, or
    // whose owner has no QR uploaded, have nowhere to send the money and
    // are refused outright.
    //
    // Checked only when ADDING. A removal can't create a conflict, and
    // blocking one would trap a guest whose selection predates the admin
    // switching this on. The whole check is a no-op while the setting is
    // off — see getOwnershipSelectionBlock in bookingUtils.js.
    // Any hour of the block already held means this tap releases it — that
    // also clears a block left half-selected by a conflict trim at checkout.
    const isRemoving = targetSlots.some(slot => (selectedDates[dateStr]?.[slot] || []).includes(courtNumber));
    if (!isRemoving) {
      // Every court already picked, across EVERY date — a multi-date
      // booking is still a single payment, so an owner clash on Tuesday
      // has to block a Thursday pick too.
      const alreadyPicked = [...new Set(
        Object.values(selectedDates).flatMap(slots => Object.values(slots).flat())
      )];
      const block = getOwnershipSelectionBlock(config, courtNumber, alreadyPicked);
      if (block) {
        showModal({ title: block.title, message: block.message, type: 'warning', confirmText: 'OK' });
        return;
      }

      // Add-on columns (Config -> Sports & Courts -> Court type "Add-on") are
      // extras like a ball machine: useless without a court to use them on, so
      // an hour on one can only be taken when the guest already holds THAT
      // hour on a paired court. Scoped to this date + this time slot only —
      // a court at 5PM is not a court at 6PM. The club's own wording explains
      // the refusal (see getAddOnRequiredMessage in bookingUtils.js).
      // Every hour of a block has to pass, since all of them are being taken.
      const addOnBlock = targetSlots
        .map(slot => getAddOnSelectionBlock(config, courtNumber, selectedDates[dateStr]?.[slot] || []))
        .find(Boolean);
      if (addOnBlock) {
        // Explaining how the add-on works, not reporting a fault the guest
        // caused — hence no warning icon, and "I understand" rather than the
        // "OK" that acknowledges an error.
        showModal({
          title: addOnBlock.title,
          message: addOnBlock.message,
          type: 'warning',
          confirmText: 'I UNDERSTAND',
          hideIcon: true,
        });
        return;
      }
    }

    setSelectedDates(prev => {
      const updatedDateSlots = { ...(prev[dateStr] || {}) };
      targetSlots.forEach((slot) => {
        const currentCourts = updatedDateSlots[slot] || [];
        let slotCourts;
        if (isRemoving) {
          if (!currentCourts.includes(courtNumber)) return;
          slotCourts = currentCourts.filter(c => c !== courtNumber);
          // Dropping the court an add-on was riding on strands that add-on — an
          // hour of ball machine with nothing to use it on, which would still
          // price and reach checkout as a billable line. It comes off with the
          // court that justified it rather than blocking the removal, so the
          // guest is never stuck unable to undo their own pick.
          const orphaned = getOrphanedAddOnCourts(config, slotCourts);
          if (orphaned.length > 0) slotCourts = slotCourts.filter(c => !orphaned.includes(c));
        } else {
          if (currentCourts.includes(courtNumber)) return;
          slotCourts = [...currentCourts, courtNumber].sort((a, b) => a - b);
        }
        if (slotCourts.length === 0) delete updatedDateSlots[slot];
        else updatedDateSlots[slot] = slotCourts;
      });
      const next = { ...prev, [dateStr]: updatedDateSlots };
      if (Object.keys(updatedDateSlots).length === 0) delete next[dateStr];
      return next;
    });
  };

  const handleClearAllDates = () => setSelectedDates({});

  // ---- Event package flow ----
  // picker (which package) -> options (which duration) -> details -> datetime.
  // Every step that has only one possible answer is skipped rather than shown
  // with a single choice in it, so a club selling one 4-hour package still
  // opens straight onto its details card.

  /**
   * Where a package's flow starts, and with which duration preselected.
   * Several durations means the option list; exactly one means skip it but
   * carry that duration forward, so the details card can name it.
   *
   * @param {object} pkg
   * @returns {{stage: string, optionId: string|null, hours: number|null}}
   */
  const packageEntryStage = (pkg) => {
    const choices = getPackageOptionChoices(pkg, null);
    if (choices.length > 1) return { stage: 'options', optionId: null, hours: null };
    if (choices.length === 1) return { stage: 'details', optionId: choices[0].id, hours: choices[0].hours };
    // Hourly-priced packages have no presets to choose between; the duration
    // is picked on the date screen as before.
    return { stage: 'details', optionId: null, hours: null };
  };

  const openPackageFlow = (onlyPackage) => {
    // Packages check availability against live bookings, which need sign-in.
    if (!bookingReady) return;
    const base = { pkg: onlyPackage || null, dateStr: '', startHour: null };
    setPkgFlow(onlyPackage
      ? { ...base, ...packageEntryStage(onlyPackage) }
      : { ...base, stage: 'picker', optionId: null, hours: null });
  };
  const closePackageFlow = () => { setPkgFlow(null); setPkgBookedSlots({}); };

  const handlePackageSelect = (pkg) => setPkgFlow(prev => ({ ...prev, pkg, ...packageEntryStage(pkg) }));

  // Choosing a duration also fixes the hours the date screen will book, so
  // the two can never disagree.
  const handlePackageOptionSelect = (optionId) => setPkgFlow((prev) => {
    const option = getPackageOptionById(prev.pkg, optionId);
    return { ...prev, optionId, hours: Number(option?.hours) || null, startHour: null };
  });
  const handlePackageOptionProceed = () => setPkgFlow(prev => ({ ...prev, stage: 'details' }));

  const handlePackageProceed = () => setPkgFlow((prev) => {
    // Pre-select the duration when there's only one on offer, so the guest
    // isn't asked a question with a single answer.
    const durations = getSelectablePackageDurations(prev.pkg, null);
    const hours = prev.hours ?? (durations.length === 1 ? durations[0] : null);
    return { ...prev, stage: 'datetime', hours };
  });

  // Back out of the details card to whichever step actually preceded it.
  const packageDetailsBack = () => {
    if (!pkgFlow?.pkg) return null;
    if (getPackageOptionChoices(pkgFlow.pkg, null).length > 1) {
      return () => setPkgFlow(prev => ({ ...prev, stage: 'options' }));
    }
    if (getActivePackages(config).length > 1) {
      return () => setPkgFlow(prev => ({ ...prev, stage: 'picker' }));
    }
    return null;
  };

  // Changing the date or duration invalidates the chosen start time — a slot
  // that was free on Tuesday says nothing about Wednesday.
  const handlePackageDateChange = (dateStr) => setPkgFlow(prev => ({ ...prev, dateStr, startHour: null }));
  const handlePackageHoursChange = (hours) => setPkgFlow(prev => ({ ...prev, hours, startHour: null }));
  const handlePackageStartChange = (startHour) => setPkgFlow(prev => ({
    ...prev,
    startHour,
    // On an hourly package the end time was measured from the OLD start, so
    // the length it implied is meaningless now — make them pick again rather
    // than silently sliding the booking later.
    hours: packageIsHourlyPriced(prev.pkg) ? null : prev.hours,
  }));
  // Hourly only: the end time IS the length. Stored as hours so everything
  // downstream — availability, pricing, the slot map — stays unchanged.
  const handlePackageEndChange = (endHour) => setPkgFlow(prev => ({
    ...prev,
    hours: endHour === null ? null : Math.max(0, Number(endHour) - Number(prev.startHour)),
  }));

  // Hands the package selection to the ORDINARY booking pipeline: the slots
  // it occupies become `selectedDates`, so slot claiming, rollback, the grid
  // and the admin screens all treat it as a normal booking. `packageBooking`
  // only overrides how it is priced and labelled.
  const handlePackageContinue = () => {
    const { pkg, dateStr, hours, startHour } = pkgFlow;
    const weekday = getWeekdayFromDateStr(dateStr);
    const price = getPackagePrice(pkg, startHour, hours, weekday);
    if (price === null) return;
    const { timeSlots, eventSlots } = buildPackageTimeSlots(config, pkg, startHour, hours);

    setSelectedDates({ [dateStr]: timeSlots });
    setPackageBooking({
      packageId: pkg.id,
      packageName: (pkg.name || '').trim() || 'Package',
      dateStr,
      startHour,
      hours,
      price,
      // The hours the guest is paying for, as opposed to the cleanup hours
      // also held in `timeSlots` — used so the processing fee and the receipt
      // count the event, not the turnover gap.
      eventSlots,
      courts: (pkg.courts || []).map(Number),
    });
    setPkgFlow(null);
    setPkgBookedSlots({});
    runStepTransition(() => setStep(3));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    // Email is optional — only validate its format if the guest entered one.
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s\-+()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showModal = (config) => setModalConfig({ isOpen: true, ...config });
  const closeModal = () => setModalConfig(prev => ({ ...prev, isOpen: false }));

  // Creates the actual Firebase booking record as soon as the guest reaches
  // checkout (not at the very end) — this is what lets the payment screen
  // show a real reference number, start the payment countdown, and generate
  // the "download reference" PDF before payment is even uploaded.
  // `equipmentRental` is passed in directly rather than read off `formData`
  // state — UserInfoForm calls setFormData(...) and onSubmit(...) back to
  // back on the same click, and since setState updates aren't applied
  // synchronously, reading formData.equipmentRental here would still see
  // the value from before the guest picked their rentals.
  //
  // Resolves to { bookingData, bookingKeys } once the booking is written, or
  // to a falsy value when it wasn't (a modal has already told the guest why)
  // — handleFormSubmit uses that to hand the booking to the checkout tab or
  // close it.
  const createPendingBooking = async (equipmentRental = null) => {
    if (firebaseError || !db) {
      showModal({
        title: 'Firebase Not Configured',
        message: 'Please configure your Firebase credentials in src/firebase.js to enable booking functionality.',
        type: 'error',
        confirmText: 'OK'
      });
      return;
    }

    setIsCreatingBooking(true);
    // Hoisted out of the try block so the catch below can still release
    // whatever was already claimed if something throws partway through —
    // e.g. an equipment claim hitting a Firebase permission error, or a
    // dropped connection right before the final write. Without this, an
    // error partway through left slots/equipment claimed but no booking
    // ever created, so the court(s) silently never re-opened.
    const claimedSlotRecords = [];
    const claimedEquipmentRecords = [];
    try {
      const dateList = Object.keys(selectedDates).sort();
      if (dateList.length === 0) {
        setIsCreatingBooking(false);
        return;
      }
      const isMultiDate = dateList.length > 1;

      // Grand total across every selected date's court cost, plus the
      // one-time processing fee and equipment rental (both charged once for
      // the whole reservation, not per date — see the write loop below).
      let courtTotal = 0;
      const perDateCourtTotal = {};
      // A package is sold as one block, not by the hour, so its price comes
      // straight from the package config — summing getCourtRate over the
      // slots it occupies would both use the wrong rates and charge for the
      // cleanup hours.
      if (packageBooking) {
        courtTotal = packageBooking.price;
        perDateCourtTotal[packageBooking.dateStr] = packageBooking.price;
      } else {
      dateList.forEach(dateStr => {
        let dateTotal = 0;
        Object.entries(selectedDates[dateStr]).forEach(([timeSlot, courts]) => {
          // dateStr matters: rate bands can be scoped to specific weekdays,
          // so the same slot can cost different amounts across the dates in
          // one multi-date booking.
          courts.forEach(court => { dateTotal += getCourtRate(config, court, timeSlot, selectedSportId, dateStr); });
        });
        perDateCourtTotal[dateStr] = dateTotal;
        courtTotal += dateTotal;
      });
      }

      // Flat by default — the configured amount, once for the reservation.
      // With "Charge per hour" ticked (Config -> Pricing) this multiplies by
      // every court-hour across every selected date, so it's computed from
      // the whole selection rather than read straight off config.
      // Event packages are exempt: they're sold at an all-in price, so no
      // surcharge is added (see calculateReservationFee).
      const bookingFee = calculateReservationFee(config, selectedDates, packageBooking);
      // Equipment rental, picked in UserInfoForm's "Need equipments?" step —
      // see EquipmentRentalModal. The same selection applies to every selected
      // date (claimed separately per date below, since each date has its own
      // session window). A "per session" item is charged once for the whole
      // reservation; a "per hour" one was already multiplied by the hours the
      // guest chose when the modal built this total, so both are read straight
      // off it rather than recomputed here.
      const equipmentTotal = equipmentRental?.total || 0;
      const grandTotal = courtTotal + bookingFee + equipmentTotal;

      const confirmationId = generateConfirmationId();
      const timeoutMinutes = Number(config?.paymentTimeoutMinutes) || 5;
      const paymentDeadline = new Date(Date.now() + timeoutMinutes * 60000).toISOString();

      // Reserve one Firebase key per date up front. A multi-date booking is
      // stored as one record PER DATE — every date-record is a normal,
      // self-contained single-date booking with its own slotLocks/
      // equipmentHolds claims, which is what lets every existing per-date
      // query and admin screen keep working unchanged. They're stitched
      // back together via a shared confirmationId and groupId (the first
      // date's own key) — see bookingGroupUtil.js.
      const dateRecords = dateList.map((dateStr, idx) => {
        const docRef = push(ref(db, 'bookings'));
        return { dateStr, bookingId: docRef.key, timeSlots: selectedDates[dateStr], groupPrimary: idx === 0 };
      });
      const groupId = isMultiDate ? dateRecords[0].bookingId : null;

      // Authoritative double-booking prevention: atomically claim every
      // selected slot on every date via a Firebase transaction (not a plain
      // read-then-write) right before creating the booking, so a slot
      // someone else grabbed in the moments since the guest reached this
      // form can't slip through — see claimBookingSlots for how the
      // atomicity works. Any date that fails rolls back every date already
      // claimed before it.
      for (const rec of dateRecords) {
        // eslint-disable-next-line no-await-in-loop
        const { granted, conflicts } = await claimBookingSlots(db, rec.dateStr, rec.timeSlots, rec.bookingId, paymentDeadline);
        if (!granted) {
          // eslint-disable-next-line no-await-in-loop
          for (const done of claimedSlotRecords) {
            await releaseBookingSlots(db, done.dateStr, done.bookingId).catch(() => {});
          }
          const conflictDetails = conflicts.map(c => `${getCourtLabel(config, c.court)} (${c.timeSlot}${isMultiDate ? `, ${rec.dateStr}` : ''})`).join(', ');
          showModal({
            title: 'Slot No Longer Available',
            message: `Sorry, the following slot${conflicts.length > 1 ? 's were' : ' was'} just booked by another guest while you were entering your details:\n\n${conflictDetails}\n\nPlease go back and select a new time slot.`,
            type: 'warning',
            confirmText: 'OK',
            onConfirm: () => {
              closeModal();
              setStep(2);
            }
          });
          setIsCreatingBooking(false);
          return;
        }
        claimedSlotRecords.push(rec);
      }

      // Same atomicity guarantee as the slot claim above, but for equipment
      // quantities within each date's own session span — see
      // claimEquipmentRental for why a transaction (not a plain read-then-
      // write) is what actually prevents two guests over-renting the last
      // few units of the same equipment for overlapping times.
      const equipmentItems = equipmentRental?.items || [];
      for (const rec of dateRecords) {
        const { startHour, endHour } = getBookingHourRange(rec.timeSlots, parseTimeSlotStartHour);
        // A "per hour" item (Config -> Equipment -> Rental type) is only held
        // for the hours the guest actually chose on THIS date — holding it
        // across the whole session would tie up units for hours nobody is
        // paying for and block other guests from renting them. An item with
        // no hours chosen on this date isn't claimed here at all. "Per
        // session" items keep the booking-wide span they always had.
        const itemsForDate = equipmentItems
          .map((item) => {
            if (item.rentalType !== 'hourly') return item;
            const slots = item.hours?.[rec.dateStr] || [];
            if (slots.length === 0) return null;
            const hours = slots.map(parseTimeSlotStartHour);
            return { ...item, startHour: Math.min(...hours), endHour: Math.max(...hours) + 1 };
          })
          .filter(Boolean);
        // eslint-disable-next-line no-await-in-loop
        const { granted: equipmentGranted, shortItem } = await claimEquipmentRental(
          db, rec.dateStr, itemsForDate, rec.bookingId, startHour, endHour, config, paymentDeadline
        );
        if (!equipmentGranted) {
          // Free every slot (and equipment) claim already made — the guest
          // never got a booking out of this attempt, so nothing should stay
          // locked for it.
          // eslint-disable-next-line no-await-in-loop
          for (const done of claimedSlotRecords) {
            await releaseBookingSlots(db, done.dateStr, done.bookingId).catch(() => {});
          }
          // eslint-disable-next-line no-await-in-loop
          for (const done of claimedEquipmentRecords) {
            await releaseEquipmentRental(db, done.dateStr, done.bookingId).catch(() => {});
          }
          showModal({
            title: 'Not Enough Equipment Available',
            message: `Sorry, there ${shortItem?.qty === 1 ? 'is' : 'are'} no longer enough ${shortItem?.label || 'equipment'} available for your selected time${isMultiDate ? ` on ${rec.dateStr}` : ''}. Please go back and adjust your rental quantity.`,
            type: 'warning',
            confirmText: 'OK',
            onConfirm: () => {
              closeModal();
              setStep(3);
            }
          });
          setIsCreatingBooking(false);
          return;
        }
        claimedEquipmentRecords.push(rec);
      }

      const trackingUrl = window.location.origin + "/bookings/" + confirmationId;
      const createdAt = getPhNow().toISOString();
      // The guest's "Partial payment" tick on Booking details. Saved on every
      // record so the admin's Reservation details can show it; the checkout
      // tab may still change it, and re-saves it when the receipt goes in.
      const guestPartialFields = buildGuestPartialPaymentFields(formData.partialPayment, config);
      const writes = {};
      dateRecords.forEach(rec => {
        writes[`bookings/${rec.bookingId}`] = {
          confirmationId,
          sportId: selectedSportId,
          ...(isMultiDate ? { groupId, groupPrimary: rec.groupPrimary, groupDateCount: dateRecords.length } : {}),
          date: rec.dateStr,
          timeSlots: rec.timeSlots,
          userName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          // Only the primary date-record's paymentAmount carries the full
          // grand total (processing fee + equipment included) — the rest just
          // carry their own court subtotal, so summing every group member's
          // paymentAmount elsewhere would double-count the shared costs.
          paymentAmount: rec.groupPrimary ? grandTotal : perDateCourtTotal[rec.dateStr],
          bookingFee,
          equipmentRental,
          paymentDeadline,
          trackingUrl,
          ...guestPartialFields,
          // Not yet a real "pending approval" booking — that only happens
          // once the guest attaches a payment receipt (see
          // finalizeSubmittedBooking in utils/checkoutFlowUtil.js).
          status: 'awaiting_payment',
          createdAt,
          // Marks this as an event package rather than hourly play. The grid
          // still renders these court-hours as BOOKED like any other
          // reservation; this is what lets the admin list, export and
          // receipts tell the two apart.
          ...(packageBooking ? {
            bookingSource: BOOKING_SOURCE_PACKAGE,
            packageId: packageBooking.packageId,
            packageName: packageBooking.packageName,
            packageHours: packageBooking.hours,
            packageStartHour: packageBooking.startHour,
            // The block's own price, stored rather than derived: every recap
            // screen shows this on the package line, and backing it out of
            // paymentAmount would break the moment anything else is added to
            // the total.
            packagePrice: packageBooking.price,
            // The hours sold, so anything reading this record can tell them
            // from the cleanup slots also present in `timeSlots`.
            packageEventSlots: packageBooking.eventSlots,
          } : {}),
        };
      });

      try {
        await update(ref(db), writes);
      } catch (writeError) {
        // The slot + equipment claims succeeded but writing the booking
        // record(s) failed — release everything so nothing stays stuck
        // locked for a booking that was never actually created.
        for (const done of claimedSlotRecords) {
          await releaseBookingSlots(db, done.dateStr, done.bookingId).catch(() => {});
        }
        for (const done of claimedEquipmentRecords) {
          await releaseEquipmentRental(db, done.dateStr, done.bookingId).catch(() => {});
        }
        claimedSlotRecords.length = 0;
        claimedEquipmentRecords.length = 0;
        throw writeError;
      }
      // No email here — the guest hasn't submitted anything yet at this
      // point (status is still 'awaiting_payment'). The one and only
      // confirmation email goes out from finalizeSubmittedBooking
      // (utils/checkoutFlowUtil.js), once they've actually attached a
      // payment receipt in the checkout tab.

      const combinedBookingData = {
        confirmationId,
        sportId: selectedSportId,
        groupId,
        userName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        paymentAmount: grandTotal,
        bookingFee,
        equipmentRental,
        paymentDeadline,
        trackingUrl,
        // So the checkout tab opens with the same "Partial payment" choice.
        ...guestPartialFields,
        status: 'awaiting_payment',
        createdAt,
        dates: dateRecords.map(rec => ({ date: rec.dateStr, timeSlots: rec.timeSlots })),
        // Legacy single-date fields, kept for anything still reading these directly.
        date: dateRecords[0].dateStr,
        timeSlots: dateRecords[0].timeSlots,
        ...(packageBooking ? {
          bookingSource: BOOKING_SOURCE_PACKAGE,
          packageId: packageBooking.packageId,
          packageName: packageBooking.packageName,
          packageHours: packageBooking.hours,
          packageStartHour: packageBooking.startHour,
          packagePrice: packageBooking.price,
          packageEventSlots: packageBooking.eventSlots,
        } : {}),
      };

      const createdKeys = dateRecords.map(rec => rec.bookingId);
      setBookingKeys(createdKeys);
      setBookingData(combinedBookingData);
      setStep(4);
      return { bookingData: combinedBookingData, bookingKeys: createdKeys };
    } catch (error) {
      console.error('Error saving booking:', error);
      // Something failed after slots (and possibly equipment) were already
      // claimed for one or more dates — e.g. claimEquipmentRental throwing
      // on a Firebase permission error rather than resolving to
      // { granted: false }. Release everything claimed so far so no court
      // stays stuck locked for a booking that was never actually created.
      for (const done of claimedSlotRecords) {
        await releaseBookingSlots(db, done.dateStr, done.bookingId).catch(() => {});
      }
      for (const done of claimedEquipmentRecords) {
        await releaseEquipmentRental(db, done.dateStr, done.bookingId).catch(() => {});
      }
      showModal({
        title: 'Booking Failed',
        message: 'Unable to save your booking. Please check your internet connection and try again.',
        type: 'error',
        confirmText: 'OK'
      });
    } finally {
      setIsCreatingBooking(false);
    }
  };

  // Minimum time (ms) the spinner stays visible for a step transition, so
  // it reads as a deliberate loading state rather than a flicker even when
  // the underlying work (a plain setStep, or a fast Firebase write)
  // finishes almost instantly.
  const MIN_TRANSITION_MS = 350;

  // Wraps a step transition (sync or async) so the modal shows a spinner
  // for at least MIN_TRANSITION_MS while `work` runs, instead of jumping
  // straight from one step's content to the next.
  const runStepTransition = (work) => {
    setStepLoading(true);
    const startedAt = Date.now();
    Promise.resolve()
      .then(() => work())
      .catch((error) => {
        console.error('Step transition error:', error);
      })
      .finally(() => {
        const remaining = MIN_TRANSITION_MS - (Date.now() - startedAt);
        if (remaining > 0) {
          setTimeout(() => setStepLoading(false), remaining);
        } else {
          setStepLoading(false);
        }
      });
  };

  /**
   * Runs a step that needs the live config — straight away when it is in,
   * otherwise once the background refresh lands (the step modal shows its
   * spinner meanwhile).
   *
   * @param {'continueToDetails'|'packageContinue'} key - which handler in
   *   liveConfigActionsRef to run.
   * @returns {void}
   */
  const runWithLiveConfig = (key) => {
    if (!configLoading) {
      liveConfigActionsRef.current[key]?.();
      return;
    }
    setPendingLiveConfigAction(key);
  };

  // Guest taps "Continue to Details" / "Reserve" — re-check every SELECTED
  // date (not just whichever one the grid happens to be showing) against a
  // fresh one-time read of that date's bookings, since bookedSlots only
  // stays live-subscribed for the currently-viewed date. This is still just
  // a fast, best-effort pre-check for a nicer error earlier in the flow;
  // the authoritative check happens again in createPendingBooking's
  // transaction right before the booking is actually written, so a failure
  // here (e.g. offline) just means the guest finds out one step later
  // instead of now rather than being blocked outright.
  const handleContinueToDetails = () => {
    runStepTransition(async () => {
      const dateStrs = Object.keys(selectedDates);
      if (dateStrs.length === 0) return;

      // A court the admin closed (Config -> Sports & Courts -> Available)
      // after the guest had already selected hours on it — the grid greys
      // those cells out live, but an in-progress selection made moments
      // earlier would otherwise still carry through to checkout.
      const closedCourtSelections = [];
      dateStrs.forEach(dateStr => {
        Object.entries(selectedDates[dateStr] || {}).forEach(([timeSlot, courtsForSlot]) => {
          (courtsForSlot || []).forEach(court => {
            if (!isCourtAvailable(config, court)) closedCourtSelections.push({ dateStr, timeSlot, court });
          });
        });
      });
      // Config -> Booking Settings. The grid already disables Reserve on
      // these, but against the config it rendered with — this runs on the
      // live one, so a rule the admin tightened since still applies.
      const ruleIssue = getSelectionRuleIssue(config, selectedDates);
      if (ruleIssue) {
        showModal({ title: 'Check Your Hours', message: ruleIssue.message, type: 'warning', confirmText: 'OK' });
        return;
      }

      if (closedCourtSelections.length > 0) {
        const names = [...new Set(closedCourtSelections.map(c => getCourtLabel(config, c.court)))].join(', ');
        showModal({
          title: 'Court Unavailable',
          message: `${names} ${closedCourtSelections.length > 1 ? 'are' : 'is'} no longer available for booking. Please remove ${closedCourtSelections.length > 1 ? 'those hours' : 'that hour'} from your selection and pick another court.`,
          type: 'warning',
          confirmText: 'OK',
        });
        return;
      }

      let allConflicts = [];
      let allClosedSlots = [];
      try {
        const perDateResults = await Promise.all(dateStrs.map(async (dateStr) => {
          const bookingsRef = ref(db, 'bookings');
          const q = query(bookingsRef, orderByChild('date'), equalTo(dateStr));
          const snap = await get(q);
          const booked = buildBookedSlotsFromSnapshot(snap);

          // The same freshness problem the closed-court check above has, one
          // level finer: the admin may have closed an individual hour after
          // the guest picked it. The live slotOverrides listener only covers
          // whichever date the grid is showing, so every SELECTED date is
          // re-read here rather than trusted from state.
          const overridesSnap = await get(ref(db, `slotOverrides/${dateStr}`));
          const overrides = overridesSnap.exists() ? overridesSnap.val() : {};
          const closed = [];
          // Hours each selected court actually opens for on this date. A
          // time-slot set scoped to particular courts (Config -> Sports &
          // Courts) can be edited while a guest sits on the grid, and the
          // cell they picked half an hour ago may no longer be one this
          // court opens for — the grid greys those out, but only on the
          // config it had when it rendered.
          const courtHours = {};
          Object.entries(selectedDates[dateStr] || {}).forEach(([timeSlot, courtsForSlot]) => {
            (courtsForSlot || []).forEach(court => {
              if (!courtHours[court]) courtHours[court] = new Set(getTimeSlotsForCourt(config, dateStr, court));
              if (isSlotClosed(overrides, timeSlot, court) || !courtHours[court].has(timeSlot)) {
                closed.push({ dateStr, timeSlot, court });
              }
            });
          });

          return {
            conflicts: findConflictsAgainstBookedSlots(booked, selectedDates[dateStr]).map(c => ({ ...c, dateStr })),
            closed,
          };
        }));
        allConflicts = perDateResults.flatMap(r => r.conflicts);
        allClosedSlots = perDateResults.flatMap(r => r.closed);
      } catch (error) {
        console.error('Error checking slot conflicts:', error);
        setStep(3);
        return;
      }

      // Reported before booking conflicts: a closed hour is the admin's
      // deliberate decision and will not free up on its own, whereas a
      // conflict is just a race the guest lost. Worded like the closed-court
      // modal above, since to the guest it is the same "you cannot have this
      // one" outcome.
      if (allClosedSlots.length > 0) {
        const details = allClosedSlots
          .map(s => `${getCourtLabel(config, s.court)} — ${formatTimeSlotRange(s.timeSlot)} (${s.dateStr})`)
          .join('\n');
        showModal({
          title: 'Time Slot Closed',
          message: `The following hour${allClosedSlots.length > 1 ? 's have' : ' has'} been closed and can no longer be booked:\n\n${details}\n\nPlease remove ${allClosedSlots.length > 1 ? 'them' : 'it'} from your selection and pick another time.`,
          type: 'warning',
          confirmText: 'OK',
        });
        return;
      }

      if (allConflicts.length > 0) {
        // Drop the now-taken slots from the guest's selection so they're
        // not silently carried forward to the details form.
        setSelectedDates(prev => {
          const next = {};
          Object.entries(prev).forEach(([dateStr, slots]) => {
            const remaining = {};
            Object.entries(slots).forEach(([timeSlot, courts]) => {
              const stillFree = courts.filter(c => !allConflicts.some(x => x.dateStr === dateStr && x.timeSlot === timeSlot && x.court === c));
              if (stillFree.length > 0) remaining[timeSlot] = stillFree;
            });
            if (Object.keys(remaining).length > 0) next[dateStr] = remaining;
          });
          return next;
        });

        const conflictDetails = allConflicts.map(c => `${getCourtLabel(config, c.court)} (${c.timeSlot}, ${c.dateStr})`).join(', ');
        showModal({
          title: 'Slot No Longer Available',
          message: `Sorry, the following slot${allConflicts.length > 1 ? 's were' : ' was'} just booked by another guest:\n\n${conflictDetails}\n\nPlease select a new time slot.`,
          type: 'warning',
          confirmText: 'OK',
        });
        return;
      }
      setStep(3);
    });
  };

  // Guest submits the details form. The checkout tab is opened right here,
  // synchronously inside the tap — browsers only allow window.open during a
  // user gesture, and by the time the booking has been written (Firebase
  // transactions per date) that gesture is gone and the tab would be
  // blocked. The tab shows a spinner until the booking is handed over, and is
  // closed again if the booking couldn't be created.
  /**
   * Creates the reservation and opens the checkout tab.
   *
   * Must run inside the guest's own tap (Continue, or "I Understand" on the
   * court rules): checkoutTab.prepare() opens the new tab synchronously, and
   * a browser blocks a tab opened outside a user gesture.
   *
   * @param {object|null} equipmentRental - from the rental step, if any.
   * @returns {void}
   */
  const proceedToCheckout = (equipmentRental) => {
    checkoutTab.prepare();
    runStepTransition(async () => {
      const created = await createPendingBooking(equipmentRental);
      if (created) {
        checkoutTab.launch(created.bookingData, created.bookingKeys);
      } else {
        checkoutTab.end();
      }
    });
  };

  // Config -> Court Reminders text for the court rules popup. Blank means
  // there is nothing to show, so the popup is skipped.
  const courtRemindersText = String(config?.[COURT_REMINDERS_TEXT_CONFIG_KEY] ?? '');

  /**
   * Continue on the guest details card. With Config -> Court Reminders ->
   * "Show court rules before checkout" on (and text to show), the rules popup opens
   * first and checkout waits for "I Understand"; otherwise straight on.
   * Validation runs first, so a guest never accepts the rules only to be sent
   * back to fix a field.
   *
   * @param {object|null} equipmentRental
   * @returns {void}
   */
  const handleFormSubmit = (equipmentRental) => {
    if (!validateForm()) return;
    if (config?.[SHOW_COURT_RULES_CONFIG_KEY] === true && courtRemindersText.trim()) {
      setPendingCourtRules({ equipmentRental });
      return;
    }
    proceedToCheckout(equipmentRental);
  };

  /**
   * "I Understand" on the court rules — carries on to checkout with the
   * rental the guest picked before the sheet opened.
   * @returns {void}
   */
  const handleCourtRulesAccept = () => {
    const pending = pendingCourtRules;
    setPendingCourtRules(null);
    proceedToCheckout(pending?.equipmentRental ?? null);
  };

  /**
   * Tells the guest their unpaid reservation was released.
   * @returns {void}
   */
  const showReservationReleasedModal = () => {
    showModal({
      title: 'Reservation Released',
      message: `Your reservation was released because payment wasn't completed within ${config?.paymentTimeoutMinutes || 5} minute(s). Please select your time slot again.`,
      type: 'warning',
      confirmText: 'OK',
      onConfirm: () => { closeModal(); setStep(2); }
    });
  };

  /**
   * Clears the finished (or abandoned) reservation from this tab.
   * @returns {void}
   */
  const clearReservation = () => {
    setBookingKeys(null);
    setBookingData(null);
    setSelectedDates({});
  };

  // The payment countdown ran out with no checkout tab left to handle it
  // (closed, or never opened) — release the reservation from here. The
  // slot is also treated as free client-side via isBookingActive() whether
  // or not this write succeeds.
  const handleBookingExpired = async () => {
    if (releasingRef.current) return;
    releasingRef.current = true;
    const keys = bookingKeys;
    const data = bookingData;
    checkoutTab.end();
    const { alreadySubmitted } = await releasePendingBooking({ db, bookingData: data, bookingKeys: keys, cancelReason: 'payment_timeout' });
    releasingRef.current = false;
    if (alreadySubmitted) {
      // The receipt went in from the checkout tab after all.
      setBookingData({ ...data, status: 'pending' });
      runStepTransition(() => setStep(5));
      return;
    }
    clearReservation();
    showReservationReleasedModal();
  };

  // Deadline reached on this tab's card. While the checkout tab is still
  // open it runs out there too and releases the booking itself.
  const handlePendingDeadline = () => {
    if (checkoutTab.isCheckoutTabOpen()) return;
    handleBookingExpired();
  };

  // Guest cancels from this tab's card. Once the receipt is in, the booking
  // is live, so this finishes to the confirmation instead of cancelling.
  const handleCheckoutCancel = () => {
    if (releasingRef.current) return;
    if (isBookingSubmitted(bookingData)) {
      checkoutTab.end();
      runStepTransition(() => setStep(5));
      return;
    }
    releasingRef.current = true;
    const keys = bookingKeys;
    const data = bookingData;
    checkoutTab.end();
    runStepTransition(async () => {
      const { alreadySubmitted } = await releasePendingBooking({ db, bookingData: data, bookingKeys: keys, cancelReason: 'guest_cancelled' });
      releasingRef.current = false;
      if (alreadySubmitted) {
        setBookingData({ ...data, status: 'pending' });
        setStep(5);
        return;
      }
      setBookingKeys(null);
      setBookingData(null);
      setStep(3);
    });
  };

  // Steps gated on the live config (see runWithLiveConfig). Assigned on every
  // render so a parked step replays with the current config and selection.
  liveConfigActionsRef.current = {
    continueToDetails: handleContinueToDetails,
    packageContinue: handlePackageContinue,
  };

  // What the checkout tab reports back (see useCheckoutTab). Assigned on
  // every render so the handlers always read current state.
  checkoutHandlersRef.current = {
    // Receipt attached in the checkout tab — the booking is submitted.
    onReceiptUploaded: (finalBookingData) => {
      if (finalBookingData) setBookingData(finalBookingData);
    },
    // "Submit Booking" — the checkout tab is closing; show the confirmation.
    onSubmitted: (finalBookingData) => {
      if (finalBookingData) setBookingData(finalBookingData);
      runStepTransition(() => setStep(5));
    },
    // Cancelled in the checkout tab (already released there) — back to the
    // details form, as the × on the old checkout modal did.
    onCancelled: () => {
      setBookingKeys(null);
      setBookingData(null);
      setStep(3);
    },
    // Timer ran out in the checkout tab (already released there).
    onExpired: () => {
      clearReservation();
      showReservationReleasedModal();
    },
    // Guest closed the checkout tab without finishing. A submitted booking
    // goes to the confirmation; an expired one is released; otherwise the
    // card offers to reopen it.
    onTabClosed: () => {
      if (isBookingSubmitted(bookingData)) {
        checkoutTab.end();
        runStepTransition(() => setStep(5));
        return;
      }
      const remaining = msUntil(bookingData?.paymentDeadline);
      if (remaining !== null && remaining <= 0) handleBookingExpired();
    },
  };

  const handleNewBooking = () => {
    setPackageBooking(null);
    setPkgFlow(null);
    setStep(2);
    setSelectedDate(getPhToday());
    setSelectedDates({});
    setBookedSlots({});
    setFormData({ fullName: '', email: '', phone: '' });
    setErrors({});
    setBookingData(null);
    setBookingKeys(null);
  };

  const goBack = () => setStep(prev => Math.max(2, prev - 1));

  const proceedToBooking = () => {
    setShowLandingPage(false);
    setStep(2);
    setSelectedDate(getPhToday());
  };

  // Entry point for the generic "Book a Court" button (header nav, mobile
  // nav) — there's only room for one button there, so a multi-sport club
  // still sees the SportPickerModal first. A single-sport club goes
  // straight into booking. LandingPage's hero and "Pick your court" cards
  // skip the modal entirely and call handleSportChosen directly instead
  // (passed down as onBookSport), since they already show one button per
  // sport.
  const handleBookNow = () => {
    if (hasMultipleSports) {
      setShowSportPicker(true);
      return;
    }
    proceedToBooking();
  };

  const handleSportChosen = (sportId) => {
    setSelectedSportId(sportId);
    setShowSportPicker(false);
    proceedToBooking();
  };

  // Returns to LandingPage from anywhere in the guest booking flow, resetting
  // all in-progress booking state. `sectionId`, when given (nav link tap —
  // Rates/Facilities/Open Play/Location/FAQ), is picked up by LandingPage's
  // scrollToId prop so the guest lands on that section instead of the top.
  const handleBackToHome = (sectionId = null) => {
    // Landing page is disabled site-wide (Config -> System) — there's no
    // page to send them to, or sections on it to scroll to, so this just
    // resets the booking flow and keeps them on TimeSlotSelection instead.
    // Rates/Facilities/Open Play/Location/FAQ don't have their own sections
    // here the way they do on the landing page, so they all land on
    // TimeSlotSelection's own "Court Information" accordion (rates, hours,
    // amenities, policies) at the bottom of the screen instead; "Home" (no
    // sectionId, or 'home') just scrolls back up to the top of the booking
    // flow like "Book a Court" does.
    if (config?.showLandingPage === false) {
      setStep(2);
      setSelectedDate(getPhToday());
      setSelectedDates({});
      setBookedSlots({});
      setFormData({ fullName: '', email: '', phone: '' });
      setErrors({});
      setBookingData(null);
      setBookingKeys(null);
      setNavOpen(false);
      if (sectionId && sectionId !== 'home') {
        scrollToCourtInformation();
      } else {
        scrollToTimeSlotSelection();
      }
      return;
    }
    setShowLandingPage(true);
    setStep(2);
    setSelectedDate(null);
    setSelectedDates({});
    setBookedSlots({});
    setFormData({ fullName: '', email: '', phone: '' });
    setErrors({});
    setBookingData(null);
    setBookingKeys(null);
    setPendingScrollId(sectionId);
    setNavOpen(false);
  };

  // "Book a Court" in the main flow header (TimeSlotSelection screen) —
  // the guest is already on the booking screen at this point (this header
  // only shows once they've left the landing page), so there's no route to
  // navigate to; just scroll them down to the court/time grid, in case
  // they've scrolled past it reading the intro/gallery section above.
  const scrollToTimeSlotSelection = () => {
    document.getElementById('booking-flow-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setNavOpen(false);
  };

  // Rates/Facilities/Open Play/Location/FAQ, when the landing page is
  // disabled — scrolls to TimeSlotSelection's own "Court Information"
  // accordion (see TimeSlotSelection.jsx's #court-information-section)
  // instead of a landing-page section that doesn't exist in this mode.
  const scrollToCourtInformation = () => {
    document.getElementById('court-information-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setNavOpen(false);
  };

  // Standalone mode - render only the booking status screen
  if (standalone) {
    return (
      <div className="app">
        <header className="lp-header">
          <div className="lp-header-inner">
            <div className="lp-brand">
              <BrandLogo src={displayConfig?.logoUrl} alt={config?.courtName || COURT_NAME} className="lp-logo" />
              <span className="lp-brand-name">{config?.courtName || COURT_NAME}</span>
            </div>

            <nav className="lp-nav">
              {NAV_LINKS.map(link => (
                <a key={link.id} className="lp-nav-link" href={link.id === 'home' ? '/' : `/#${link.id}`}>
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Already on the Track Booking screen — offer "Book a Court"
                (straight into TimeSlotSelection, not the landing page)
                instead of linking to itself. */}
            <a href="/?book=1" className="lp-book-btn">Book a Court</a>
            <button type="button" className="lp-nav-toggle" onClick={() => setNavOpen(v => !v)} aria-label="Toggle menu">
              {navOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>

          {navOpen && (
            <nav className="lp-nav-mobile">
              {NAV_LINKS.map(link => (
                <a key={link.id} className="lp-nav-link" href={link.id === 'home' ? '/' : `/#${link.id}`} onClick={() => setNavOpen(false)}>
                  {link.label}
                </a>
              ))}
              <a href="/?book=1" className="lp-book-btn" onClick={() => setNavOpen(false)}>Book a Court</a>
            </nav>
          )}
        </header>
        <main className="main-content">
          <BookingStatusScreen />
        </main>
        <AppFooter courtName={config?.courtName} />
      </div>
    );
  }

  return (
    // Hands `config` (snapshot/cache during the live load, live after) to the
    // screens below, which read it through useConfig() themselves. Always
    // rendered, so nothing remounts when the live config lands.
    <ConfigOverride config={config}>
      <div className="app">
        <SportPickerModal
          isOpen={showSportPicker}
          onClose={() => setShowSportPicker(false)}
          sports={config?.sports || []}
          onSelect={handleSportChosen}
        />
        {showLandingPage ? (
          // Rendered here during the live load too (canShowEarly), so it stays
          // mounted — no reset of scroll, menus or modals — when the live config
          // replaces the snapshot. Book goes straight to the grid, which shows
          // its loading overlay until the selected date's bookings are in.
          <LandingPage
            onBookNow={handleBookNow}
            onBookSport={handleSportChosen}
            scrollToId={pendingScrollId}
            onScrolledToSection={() => setPendingScrollId(null)}
          />
        ) : (
          <>
            <header className="lp-header">
              <div className="lp-header-inner">
                {/* Clicking the brand goes back to the landing page, the
                    convention guests expect from a site header. Only made
                    interactive when there IS a landing page to go to —
                    handleBackToHome() no-ops into staying on this screen when
                    it's disabled (Config -> System), so a clickable logo
                    would otherwise look broken. */}
                <div
                  className={`lp-brand${landingPageEnabled ? ' lp-brand-link' : ''}`}
                  {...(landingPageEnabled ? {
                    role: 'button',
                    tabIndex: 0,
                    onClick: () => handleBackToHome(),
                    onKeyDown: (e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleBackToHome(); }
                    },
                  } : {})}
                >
                  <BrandLogo src={displayConfig?.logoUrl} alt={config?.courtName || COURT_NAME} className="lp-logo" />
                  <span className="lp-brand-name">{config?.courtName || COURT_NAME}</span>
                </div>

                <nav className="lp-nav">
                  {NAV_LINKS.map(link => (
                    <button key={link.id} type="button" className="lp-nav-link" onClick={() => handleBackToHome(link.id)}>
                      {link.label}
                    </button>
                  ))}
                  <button type="button" className="lp-nav-link" onClick={() => setShowTrackBookingModal(true)}>
                    Track Booking
                  </button>
                </nav>

                <button type="button" className="lp-book-btn" onClick={scrollToTimeSlotSelection}>Book a Court</button>
                <button type="button" className="lp-nav-toggle" onClick={() => setNavOpen(v => !v)} aria-label="Toggle menu">
                  {navOpen ? <X size={22} /> : <Menu size={22} />}
                </button>
              </div>

              {navOpen && (
                <nav className="lp-nav-mobile">
                  {NAV_LINKS.map(link => (
                    <button key={link.id} type="button" className="lp-nav-link" onClick={() => handleBackToHome(link.id)}>
                      {link.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="lp-nav-link"
                    onClick={() => { setShowTrackBookingModal(true); setNavOpen(false); }}
                  >
                    Track Booking
                  </button>
                  <button type="button" className="lp-book-btn" onClick={scrollToTimeSlotSelection}>Book a Court</button>
                </nav>
              )}
            </header>

            {step === 2 && isUiDesignV2 && (
              <BookingIntroV2
                config={config}
                sportId={selectedSportId}
                sportName={activeSport?.name}
                clubName={config?.courtName || COURT_NAME}
                photos={visibleGalleryPhotos}
                totalPhotos={galleryPhotos.length}
                onOpenPhoto={setLightboxIndex}
                packageCta={<PackageEntryButton onClick={openPackageFlow} />}
              />
            )}

            {step === 2 && !isUiDesignV2 && (
              <section className="booking-intro">
                {/* Renders nothing unless the club has an active package (see
                    PackageEntryButton), so an ordinary club's booking screen is
                    unchanged. */}
                <div className="booking-package-cta">
                  <PackageEntryButton onClick={openPackageFlow} />
                </div>
                {mainGalleryPhoto && (
                  <div className={`booking-gallery${sideGalleryPhotos.length === 0 ? ' single' : ''}`}>
                    <img
                      className="bg-main"
                      src={mainGalleryPhoto.url}
                      alt="Court view 1"
                      onClick={() => setLightboxIndex(0)}
                    />
                    {sideGalleryPhotos.length > 0 && (
                      <div className="bg-side" style={{ '--gallery-side-count': sideGalleryPhotos.length }}>
                        {sideGalleryPhotos.map((photo, i) => (
                          <img
                            key={photo.id || photo.url}
                            src={photo.url}
                            alt={`Court view ${i + 2}`}
                            onClick={() => setLightboxIndex(i + 1)}
                          />
                        ))}
                      </div>
                    )}
                    {galleryPhotos.length > 1 && (
                      <button type="button" className="gallery-showall" onClick={() => setLightboxIndex(0)}>
                        <LayoutGrid size={16} />
                        <span>Show all photos</span>
                      </button>
                    )}
                  </div>
                )}
                <h1>
                  Book a {activeSport?.name || 'Court'}{activeSport ? ' Court' : ''} at{' '}
                  <span className="accent">{config?.courtName || COURT_NAME}</span>
                </h1>
                <BookingInfoBar
                  locationText={config?.locationText}
                  mapLinkUrl={getMapLinkUrl(config)}
                  contactNumber={config?.contactNumber}
                />
              </section>
            )}

            {lightboxIndex !== null && galleryPhotos[lightboxIndex] && (
              <div className="booking-lightbox" onClick={() => setLightboxIndex(null)}>
                <button className="lb-close" onClick={() => setLightboxIndex(null)} aria-label="Close"><X size={22} /></button>
                {galleryPhotos.length > 1 && (
                  <button
                    className="lb-nav lb-prev"
                    aria-label="Previous photo"
                    onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => (i - 1 + galleryPhotos.length) % galleryPhotos.length); }}
                  ><ChevronLeft size={22} /></button>
                )}
                <div className="lb-stage" onClick={(e) => e.stopPropagation()}>
                  <img src={galleryPhotos[lightboxIndex].url} alt={`Court view ${lightboxIndex + 1}`} />
                  {galleryPhotos.length > 1 && (
                    <div className="lb-counter">{lightboxIndex + 1} / {galleryPhotos.length}</div>
                  )}
                </div>
                {galleryPhotos.length > 1 && (
                  <button
                    className="lb-nav lb-next"
                    aria-label="Next photo"
                    onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => (i + 1) % galleryPhotos.length); }}
                  ><ChevronRight size={22} /></button>
                )}
              </div>
            )}

            <main className="main-content">
              <>

                <div className="booking-flow" id="booking-flow-section">
                  {/* The base "page" for the whole flow. Stays mounted (and
                      visible, dimmed) behind the details/checkout/confirmation
                      modal below once the guest moves past it, so there's
                      always a page for that modal to sit on top of. The id
                      here is what the header's "Book a Court" button
                      scrolls to (see scrollToTimeSlotSelection below). */}
                  {step >= 2 && (
                    <BookingTimeSlotScreen
                      selectedDate={selectedDate}
                      selectedDates={selectedDates}
                      sportId={selectedSportId}
                      onDateSelect={handleDateSelect}
                      onCourtToggle={handleCourtToggle}
                      onClearAll={handleClearAllDates}
                      bookedSlots={bookedSlots}
                      slotOverrides={slotOverrides}
                      nextDayBookedSlots={nextDayBookedSlots}
                      nextDaySlotOverrides={nextDaySlotOverrides}
                      onJoinOpenPlay={handleJoinOpenPlaySlot}
                      onOpenPlaySessionClick={handleOpenPlaySessionClick}
                      onContinue={() => runWithLiveConfig('continueToDetails')}
                      canContinue={Object.keys(selectedDates).length > 0}
                      // Only the selected date's bookings hold the cells
                      // back — without them a booked slot would read as free.
                      // Rates and courts come from the build config, so the
                      // live config refresh is not waited on.
                      isFetchingSlots={isFetchingSlots}
                      fetchTimedOut={slotsTimedOut}
                      onRetryFetch={handleSlotsRetry}
                    />
                  )}
                </div>
              </>
            </main>

            {/* Guest details, checkout, and confirmation are presented as a
                modal on top of the date/time page above (per the jassen
                screens, which read as an overlay dialog) instead of swapping
                out the main content area. */}
            {/* The package screens sit in the same overlay as details/checkout/
                confirmation, so the flow reads as one dialog throughout. */}
            {pkgFlow && (
              <div className="screen-modal-overlay">
                <div className="screen-modal-inner">
                  {pkgFlow.stage === 'picker' && (
                    <PackagePickerModal onSelect={handlePackageSelect} onClose={closePackageFlow} />
                  )}
                  {pkgFlow.stage === 'options' && (
                    <PackageOptionPickerModal
                      pkg={pkgFlow.pkg}
                      selectedOptionId={pkgFlow.optionId}
                      onSelectOption={handlePackageOptionSelect}
                      onProceed={handlePackageOptionProceed}
                      // No Back when this is the first screen — a club with one
                      // package has no picker behind it.
                      onBack={getActivePackages(config).length > 1
                        ? () => setPkgFlow(prev => ({ ...prev, stage: 'picker' }))
                        : null}
                      onClose={closePackageFlow}
                    />
                  )}
                  {pkgFlow.stage === 'details' && (
                    <PackageDetailsModal
                      pkg={pkgFlow.pkg}
                      option={getPackageOptionById(pkgFlow.pkg, pkgFlow.optionId)}
                      onProceed={handlePackageProceed}
                      // No Back when nothing precedes this screen.
                      onBack={packageDetailsBack()}
                      onClose={closePackageFlow}
                    />
                  )}
                  {pkgFlow.stage === 'datetime' && (
                    <PackageDateTimeModal
                      pkg={pkgFlow.pkg}
                      minDate={formatInPhTime(getPhToday(), 'yyyy-MM-dd')}
                      dateStr={pkgFlow.dateStr}
                      hours={pkgFlow.hours}
                      startHour={pkgFlow.startHour}
                      bookedSlots={pkgBookedSlots}
                      loadingSlots={pkgLoadingSlots}
                      // The duration came from the option step; the date screen
                      // shows it rather than asking again. Only when that step
                      // was actually shown — with a single option there is no
                      // choice to send the guest back to.
                      lockHours={!!pkgFlow.optionId && getPackageOptionChoices(pkgFlow.pkg, null).length > 1}
                      // Only rule out past hours when the event is TODAY.
                      currentHour={pkgFlow.dateStr === formatInPhTime(getPhToday(), 'yyyy-MM-dd')
                        ? parseInt(formatInPhTime(new Date(), 'H'), 10)
                        : null}
                      onDateChange={handlePackageDateChange}
                      onHoursChange={handlePackageHoursChange}
                      onStartChange={handlePackageStartChange}
                      onEndChange={handlePackageEndChange}
                      onContinue={() => runWithLiveConfig('packageContinue')}
                      onBack={() => setPkgFlow(prev => ({ ...prev, stage: 'details' }))}
                      onClose={closePackageFlow}
                    />
                  )}
                </div>
              </div>
            )}

            {(stepLoading || pendingLiveConfigAction || step >= 3) && (
              <div className="screen-modal-overlay">
                <div className="screen-modal-inner">
                  {(stepLoading || pendingLiveConfigAction) ? (
                    // A step transition is in flight (Continue to Details / submit
                    // details / Payment Done), or Continue is waiting on the
                    // live config — show the spinner in place of whichever
                    // step's content would otherwise render, so the modal
                    // never flashes empty or snaps straight to the next step.
                    <div className="screen-modal-loading">
                      {spinnerRing('Just a moment...')}
                    </div>
                  ) : (
                    <>
                      {step === 3 && <UserInfoForm formData={formData} setFormData={setFormData} errors={errors} onSubmit={handleFormSubmit} selectedDates={selectedDates} sportId={selectedSportId} onBack={goBack} submitting={isCreatingBooking} packageBooking={packageBooking} />}
                      {/* Payment happens in the checkout tab; this card waits for it. */}
                      {step === 4 && bookingData && (
                        <CheckoutTabPending
                          bookingData={bookingData}
                          tabState={checkoutTab.tabState}
                          onOpenCheckout={checkoutTab.reopen}
                          onCancel={handleCheckoutCancel}
                          onExpire={handlePendingDeadline}
                        />
                      )}
                      {step === 5 && bookingData && <ConfirmationScreen bookingData={bookingData} onNewBooking={handleNewBooking} />}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Between the guest details and checkout, when Config -> Court
                Reminders -> "Show court rules before checkout" is on. × returns to the
                details card; "I Understand" carries on to checkout. */}
            <CourtRulesSheet
              isOpen={!!pendingCourtRules}
              text={courtRemindersText}
              onAccept={handleCourtRulesAccept}
              onClose={() => setPendingCourtRules(null)}
            />

            <Modal isOpen={modalConfig.isOpen} onClose={closeModal} title={modalConfig.title} message={modalConfig.message} type={modalConfig.type} confirmText={modalConfig.confirmText} onConfirm={modalConfig.onConfirm} showCancel={modalConfig.showCancel} cancelText={modalConfig.cancelText} hideIcon={modalConfig.hideIcon} />

            {/* "Join this session" — session details, who's coming, checkout
                and confirmation, all inside one modal. Writes to
                openPlayJoins, never to bookings: guest availability is derived
                from bookings' timeSlots, so a booking record here would flip
                the Open Play cell to "Booked" and shut everyone else out of
                the very session it was advertising. */}
            <OpenPlayModal
              isOpen={!!openPlayJoin}
              onClose={() => setOpenPlayJoin(null)}
              config={config}
              sports={config?.sports || []}
              initialSportId={openPlayJoin?.sportId || null}
              initialDate={openPlayJoin?.dateStr || null}
              initialTimeSlot={openPlayJoin?.timeSlot || null}
              session={openPlayJoin?.session || null}
            />

            <TrackBookingModal isOpen={showTrackBookingModal} onClose={() => setShowTrackBookingModal(false)} />

            {/* Dynamic system footer utilizing context data */}
            <AppFooter courtName={config?.courtName} />
          </>
        )}

        {/* Sign-in failed or a needed config read stalled, while this guest
            could already render from the snapshot/cache: keep the page they
            were on and float the error modal over it. */}
        {connectionFailed && <ConnectionErrorScreen />}
      </div>
    </ConfigOverride>
  );
}

// ==================== 2. ROOT SYSTEM APP ENTRY ====================
// This component safely holds the provider at the very tip top of the DOM tree
function App() {
  return (
    // AuthProvider starts the anonymous sign-in without blocking the tree.
    // Routes that read the database on mount stay behind AuthGuard; the
    // guest landing route does not, so it can paint before sign-in finishes.
    <AuthProvider>
      <ConfigProvider>
        {/* Welcome overlay on every guest and admin page load, for a fixed 4s.
            Rendered alongside the app, not around it, so App Check, sign-in
            and the first screen load behind it. */}
        <FirstVisitSplash />
        <Router>
          <InAppBrowserBanner />
          <FacebookBrowserPrompt />
          <Routes>
            <Route path={ADMIN_ROUTE_PATH} element={<AuthGuard><AdminPage /></AuthGuard>} />
            {/* Point of Sale — its own staff sign-in, separate from the
                booking admin (see PosPage.jsx). */}
            <Route path={POS_ROUTE_PATH} element={<AuthGuard><PosPage /></AuthGuard>} />
            {/* Checkout (payment) tab, opened from the booking flow — see
                hooks/useCheckoutTab.js. */}
            <Route path={`${CHECKOUT_ROUTE_PATH}/:sessionId`} element={<AuthGuard><CheckoutPage /></AuthGuard>} />
            <Route path={`${OPEN_PLAY_CHECKOUT_ROUTE_PATH}/:sessionId`} element={<AuthGuard><OpenPlayCheckoutPage /></AuthGuard>} />
            <Route path="/bookings/:bookingId" element={<AuthGuard><BookingApp standalone /></AuthGuard>} />
            <Route path="/bookings" element={<AuthGuard><BookingApp standalone /></AuthGuard>} />
            {/* In-app browser UI preview — reads no data, so no AuthGuard. */}
            <Route path={UI_PREVIEW_ROUTE_PATH} element={<BrowserUiPreviewPage />} />
            <Route path="/*" element={<BookingApp />} />
          </Routes>
        </Router>
      </ConfigProvider>
    </AuthProvider>
  );
}

export default App;